import type { GameState, GameEvent, Side } from '../types/game-state';
import { createWorldState } from './world-data';
import { createUSArsenal, createUSSRArsenal } from './arsenal';

function createInitialGameState(playerSide: Side): GameState {
  return {
    phase: 'briefing',
    currentRound: 1,
    maxRounds: 6,
    playerSide,
    defconLevel: 5,
    world: createWorldState(),
    usArsenal: createUSArsenal(),
    ussrArsenal: createUSSRArsenal(),
    strikeHistory: [],
    gameResult: null,
  };
}

function escalateDefcon(state: GameState): GameState {
  if (state.defconLevel <= 1) return state;
  const newLevel = (state.defconLevel - 1) as 1 | 2 | 3 | 4;
  return { ...state, defconLevel: newLevel };
}

function transition(state: GameState, event: GameEvent): GameState {
  switch (event.type) {
    case 'BOOT_COMPLETE':
      return { ...state, phase: 'login' };

    case 'LOGIN_SUCCESS':
      return { ...state, phase: 'game_select' };

    case 'GAME_SELECTED':
      return { ...state, phase: 'side_select' };

    case 'SIDE_SELECTED':
      return { ...state, phase: 'briefing', playerSide: event.side };

    case 'BRIEFING_ACKNOWLEDGED':
      return { ...state, phase: 'allocation', defconLevel: 3 };

    case 'START_ALLOCATION':
      return { ...state, phase: 'allocation' };

    case 'ALLOCATION_CONFIRMED':
      return { ...state, phase: 'targeting' };

    case 'START_TARGETING':
      return { ...state, phase: 'targeting' };

    case 'TARGETS_CONFIRMED':
      return { ...state, phase: 'launch', defconLevel: 2 };

    case 'START_LAUNCH':
      return { ...state, phase: 'launch' };

    case 'LAUNCH_COMPLETE': {
      const newState = { ...state, phase: 'counterstrike' as const, defconLevel: 1 as const };
      // Apply results to world state
      for (const result of event.results) {
        applyStrikeResult(newState, result);
      }
      return newState;
    }

    case 'START_COUNTERSTRIKE':
      return { ...state, phase: 'counterstrike' };

    case 'COUNTERSTRIKE_COMPLETE': {
      const newState = { ...state, phase: 'assessment' as const };
      for (const result of event.results) {
        applyStrikeResult(newState, result);
      }
      // Record strikes
      const records = [...state.strikeHistory];
      if (event.aiStrike) {
        records.push({
          attackerSide: event.aiStrike.attackerSide,
          round: state.currentRound,
          results: event.results,
        });
      }
      return { ...newState, strikeHistory: records };
    }

    case 'ASSESSMENT_ACKNOWLEDGED':
      return { ...state, phase: 'next_round_prompt' };

    case 'NEXT_ROUND':
      return {
        ...state,
        phase: 'allocation',
        currentRound: state.currentRound + 1,
        defconLevel: Math.min(3, state.defconLevel + 1) as 1 | 2 | 3 | 4 | 5,
      };

    case 'END_GAME':
      return { ...state, phase: 'endgame', gameResult: event.result };
  }
}

function applyStrikeResult(
  state: GameState,
  result: { targetId: string; targetType: string; casualties: number; infrastructureDamage: number; targetDestroyed: boolean },
): void {
  if (result.targetType === 'city') {
    const allCities = [...state.world.usCities, ...state.world.ussrCities];
    const city = allCities.find(c => c.id === result.targetId);
    if (city) {
      city.currentPopulation = Math.max(0, city.currentPopulation - result.casualties);
      city.infrastructureLevel = Math.max(0, city.infrastructureLevel - result.infrastructureDamage);
      if (result.targetDestroyed) city.isDestroyed = true;
    }
  } else {
    const allInstallations = [...state.world.usInstallations, ...state.world.ussrInstallations];
    const installation = allInstallations.find(i => i.id === result.targetId);
    if (installation && result.targetDestroyed) {
      installation.isDestroyed = true;
      // Destroy weapons at this base
      const allWeapons = [...state.usArsenal.weapons, ...state.ussrArsenal.weapons];
      for (const weapon of allWeapons) {
        if (weapon.baseId === result.targetId && weapon.status === 'ready') {
          weapon.status = 'destroyed';
        }
      }
    }
  }
}

export { createInitialGameState, transition, escalateDefcon };
